
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  private retryTimeout?: NodeJS.Timeout;

  public state: State = {
    hasError: false,
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    console.error('ErrorBoundary: getDerivedStateFromError called with:', error);
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    
    this.setState({ errorInfo });
    
    // Report error to console with context
    console.group('Error Boundary Details');
    console.error('Error:', error);
    console.error('Error Message:', error.message);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Is Mobile Device:', /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    console.error('User Agent:', navigator.userAgent);
    console.error('Window dimensions:', window.innerWidth, 'x', window.innerHeight);
    console.groupEnd();
  }

  private handleRetry = () => {
    console.log('ErrorBoundary: Retry attempt', this.state.retryCount + 1);
    if (this.state.retryCount < 3) {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: prevState.retryCount + 1
      }));
    } else {
      console.log('ErrorBoundary: Max retries reached, forcing page refresh');
      // If retry limit reached, suggest page refresh
      window.location.reload();
    }
  };

  private handleRefresh = () => {
    console.log('ErrorBoundary: Manual page refresh triggered');
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="text-center p-6 bg-white rounded-lg shadow-lg max-w-sm w-full">
            <div className="mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-lg font-bold text-gray-900 mb-2">Something went wrong</h1>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                We encountered an unexpected error. You can try again or refresh the page.
              </p>
            </div>

            <div className="space-y-3">
              {this.state.retryCount < 3 && (
                <button 
                  onClick={this.handleRetry}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  Try Again ({3 - this.state.retryCount} attempts left)
                </button>
              )}
              
              <button 
                onClick={this.handleRefresh}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium text-sm"
              >
                Refresh Page
              </button>

              <button 
                onClick={() => window.location.href = window.location.origin}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                Go to Homepage
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                  Developer Info
                </summary>
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                  <div className="font-medium text-red-600 mb-1">Error:</div>
                  <div className="mb-2 font-mono break-all">{this.state.error.message}</div>
                  {this.state.errorInfo && (
                    <>
                      <div className="font-medium text-red-600 mb-1">Component Stack:</div>
                      <pre className="whitespace-pre-wrap text-xs overflow-x-auto">{this.state.errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }

  public componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }
}
